"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Airdrop = exports.ORCA_VERIFIER_PK = exports.GOVERNANCE_VERIFIER_PK = exports.MERKLE_VERIFIER_PK = exports.PASSWORD_VERIFIER_PK = exports.BASIC_VERIFIER_PK = exports.AIRDROP_PK = void 0;
const crypto_1 = __importDefault(require("crypto"));
const anchor_1 = require("@coral-xyz/anchor");
const spl_token_1 = require("@solana/spl-token");
const web3_js_1 = require("@solana/web3.js");
const bs58_1 = __importDefault(require("bs58"));
const js_sha3_1 = require("js-sha3");
const dual_airdrop_json_1 = __importDefault(require("./dual_airdrop.json"));
const basic_verifier_json_1 = __importDefault(require("./basic_verifier.json"));
const password_verifier_json_1 = __importDefault(require("./password_verifier.json"));
const merkle_verifier_json_1 = __importDefault(require("./merkle_verifier.json"));
const governance_verifier_json_1 = __importDefault(require("./governance_verifier.json"));
const orca_verifier_json_1 = __importDefault(require("./orca_verifier.json"));
const balance_tree_1 = require("./utils/balance_tree");
const utils_1 = require("./utils/utils");
const governance_1 = require("./utils/governance");
const deserialize_1 = require("./utils/deserialize");
exports.AIRDROP_PK = new web3_js_1.PublicKey('2fJcpdR6qzqDP7fBqvoJQ5PGYdaRFBNyUKZkZf5t12mr');
exports.BASIC_VERIFIER_PK = new web3_js_1.PublicKey('FEdxZUg4BtWvMy7gy7pXEoj1isqBRYmbYdpyZfq5QZYr');
exports.PASSWORD_VERIFIER_PK = new web3_js_1.PublicKey('EmsREpwoUtHnmg8aSCqmTFyfp71vnnFCdZozohcrZPeL');
exports.MERKLE_VERIFIER_PK = new web3_js_1.PublicKey('8tBcmZAMNm11DuGAS2r6PqSA3CKt72amoz8bVj14xRiT');
exports.GOVERNANCE_VERIFIER_PK = new web3_js_1.PublicKey('ATCsJvzSbHaJj3a9uKTRHSoD8ZmWPfeC3sYxzcJJHTM5');
exports.ORCA_VERIFIER_PK = new web3_js_1.PublicKey('9X1uDdEsKpc7s1WdZzmfzLG5nhnf2KuE5WpaDaGjGyiG');
/**
 * API class with functions to interact with the Airdrop Program
 */
class Airdrop {
    /**
     * Create an Airdrop object
     *
     * @param rpcUrl The solana cluster endpoint used for the connecton
     */
    constructor(rpcUrl, commitment = 'finalized') {
        this.commitment = commitment;
        this.connection = new web3_js_1.Connection(rpcUrl, this.commitment || 'finalized');
        const opts = {
            preflightCommitment: 'finalized',
            commitment: 'finalized',
        };
        // Public key and payer not actually needed since this does not send transactions.
        const wallet = {
            publicKey: exports.AIRDROP_PK,
            signAllTransactions: (txs) => __awaiter(this, void 0, void 0, function* () { return txs; }),
            signTransaction: (tx) => __awaiter(this, void 0, void 0, function* () { return tx; }),
            payer: new web3_js_1.Keypair(),
        };
        const provider = new anchor_1.AnchorProvider(this.connection, wallet, opts);
        this.airdropProgram = new anchor_1.Program(dual_airdrop_json_1.default, exports.AIRDROP_PK, provider);
        this.basicVerifierProgram = new anchor_1.Program(basic_verifier_json_1.default, exports.BASIC_VERIFIER_PK, provider);
        this.passwordVerifierProgram = new anchor_1.Program(password_verifier_json_1.default, exports.PASSWORD_VERIFIER_PK, provider);
        this.merkleVerifierProgram = new anchor_1.Program(merkle_verifier_json_1.default, exports.MERKLE_VERIFIER_PK, provider);
        this.governanceVerifierProgram = new anchor_1.Program(governance_verifier_json_1.default, exports.GOVERNANCE_VERIFIER_PK, provider);
        this.orcaVerifierProgram = new anchor_1.Program(orca_verifier_json_1.default, exports.ORCA_VERIFIER_PK, provider);
    }
    getVaultAddress(state) {
        const [vault, _bump] = anchor_1.web3.PublicKey.findProgramAddressSync([
            Buffer.from(anchor_1.utils.bytes.utf8.encode('Vault')),
            state.toBuffer(),
        ], this.airdropProgram.programId);
        return vault;
    }
    fetchOrcaVerifierState(verifierState) {
        return __awaiter(this, void 0, void 0, function* () {
            const verifierStateObj = yield this.orcaVerifierProgram.account.verifierState.fetch(verifierState);
            return verifierStateObj;
        });
    }
    /**
     * Create a transaction with the instructions for setting up a basic airdrop.
     * This includes the config as well as transferring the tokens.
     */
    createConfigBasicTransaction(source, amount, authority) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const airdropSeed = crypto_1.default.randomBytes(32);
            const [airdropState, _airdropStateBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([airdropSeed], this.airdropProgram.programId));
            const basicVault = this.getVaultAddress(airdropState);
            const { mint } = yield (0, spl_token_1.getAccount)(this.connection, source, 'single');
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([airdropState.toBuffer()], this.basicVerifierProgram.programId);
            const basicConfigureIx = yield this.airdropProgram.methods.configure(airdropSeed)
                .accounts({
                payer: authority,
                closeAuthority: authority,
                verifierSignature,
                vault: basicVault,
                mint,
                state: airdropState,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                systemProgram: anchor_1.web3.SystemProgram.programId,
                rent: anchor_1.web3.SYSVAR_RENT_PUBKEY,
            })
                .instruction();
            transaction.add(basicConfigureIx);
            const transferIx = (0, spl_token_1.createTransferInstruction)(source, basicVault, authority, amount);
            transaction.add(transferIx);
            // No verifier state is actually needed.
            return {
                transaction,
                airdropState,
                verifierState: airdropState,
            };
        });
    }
    /**
     * Create a transaction with the instructions for setting up a password airdrop.
     * This includes the config as well as transferring the tokens.
     */
    createConfigPasswordTransaction(source, amount, authority, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const airdropSeed = crypto_1.default.randomBytes(32);
            const verifierSeed = crypto_1.default.randomBytes(32);
            const [passwordAirdropState, _passwordAirdropStateBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([airdropSeed], this.airdropProgram.programId));
            const [passwordVerifierState, _passwordVerifierBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([verifierSeed], this.passwordVerifierProgram.programId));
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([passwordAirdropState.toBuffer()], this.passwordVerifierProgram.programId);
            const { mint } = yield (0, spl_token_1.getAccount)(this.connection, source, 'single');
            const passwordVault = this.getVaultAddress(passwordAirdropState);
            const passwordConfigureIx = yield this.airdropProgram.methods.configure(airdropSeed)
                .accounts({
                payer: authority,
                closeAuthority: authority,
                state: passwordAirdropState,
                vault: passwordVault,
                mint,
                verifierSignature,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                systemProgram: anchor_1.web3.SystemProgram.programId,
                rent: anchor_1.web3.SYSVAR_RENT_PUBKEY,
            })
                .instruction();
            // First instruction configures the airdrop program.
            transaction.add(passwordConfigureIx);
            const passwordInitIx = yield this.passwordVerifierProgram.methods.init(verifierSeed, Buffer.from(js_sha3_1.keccak_256.digest(Buffer.from(password))))
                .accounts({
                authority,
                verifierState: passwordVerifierState,
                airdropState: passwordAirdropState,
                systemProgram: anchor_1.web3.SystemProgram.programId,
                rent: anchor_1.web3.SYSVAR_RENT_PUBKEY,
            })
                .instruction();
            // Next instruction configures the password.
            transaction.add(passwordInitIx);
            // Finally transfer tokens into the vault.
            const transferIx = (0, spl_token_1.createTransferInstruction)(source, passwordVault, authority, amount);
            transaction.add(transferIx);
            return {
                transaction,
                airdropState: passwordAirdropState,
                verifierState: passwordVerifierState,
            };
        });
    }
    /**
     * Create a transaction with the instructions for setting up a merkle airdrop.
     * This includes the config as well as transferring the tokens.
     */
    createConfigMerkleTransaction(source, authority, totalAmount, merkleRoot, amountsByRecipient, closeAuthority) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const { mint } = yield (0, spl_token_1.getAccount)(this.connection, source, 'single');
            const airdropSeed = crypto_1.default.randomBytes(32);
            const verifierSeed = crypto_1.default.randomBytes(32);
            const [merkleAirdropState, _merkleAirdropStateBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([airdropSeed], this.airdropProgram.programId));
            const [merkleVerifierState, _merkleVerifierBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([verifierSeed], this.merkleVerifierProgram.programId));
            const merkleVault = this.getVaultAddress(merkleAirdropState);
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([merkleVerifierState.toBuffer()], this.merkleVerifierProgram.programId);
            const merkleConfigureIx = yield this.airdropProgram.methods.configure(airdropSeed)
                .accounts({
                payer: authority,
                closeAuthority: closeAuthority !== null && closeAuthority !== void 0 ? closeAuthority : authority,
                verifierSignature,
                vault: merkleVault,
                mint,
                state: merkleAirdropState,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                systemProgram: anchor_1.web3.SystemProgram.programId,
                rent: anchor_1.web3.SYSVAR_RENT_PUBKEY,
            })
                .instruction();
            // First instruction configures the airdrop program.
            transaction.add(merkleConfigureIx);
            let root = [];
            if (amountsByRecipient) {
                const tree = new balance_tree_1.BalanceTree(amountsByRecipient);
                root = (0, utils_1.toBytes32Array)(tree.getRoot());
            }
            else if (merkleRoot) {
                root = merkleRoot;
            }
            else {
                throw new Error('Merkle root of amounts by recipient required');
            }
            const merkleInitIx = yield this.merkleVerifierProgram.methods.init(verifierSeed, root)
                .accounts({
                payer: authority,
                state: merkleVerifierState,
                airdropState: merkleAirdropState,
                systemProgram: anchor_1.web3.SystemProgram.programId,
            })
                .instruction();
            // Next instruction configures the merkle tree.
            transaction.add(merkleInitIx);
            const transferIx = (0, spl_token_1.createTransferInstruction)(source, merkleVault, authority, totalAmount);
            transaction.add(transferIx);
            return {
                transaction,
                airdropState: merkleAirdropState,
                verifierState: merkleVerifierState,
            };
        });
    }
    /**
     * Create a transaction with the instructions for setting up a governance
     * airdrop. This includes the config as well as transferring the tokens.
     */
    createConfigGovernanceTransaction(source, authority, totalAmount, amountPerVoter, eligibilityStart, eligibilityEnd, governance) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const { mint } = yield (0, spl_token_1.getAccount)(this.connection, source, 'single');
            const airdropSeed = crypto_1.default.randomBytes(32);
            const verifierSeed = crypto_1.default.randomBytes(32);
            const [governanceAirdropState, _governanceAirdropStateBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([airdropSeed], this.airdropProgram.programId));
            const [governanceVerifierState, _governanceVerifierBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([verifierSeed], this.governanceVerifierProgram.programId));
            const governanceVault = this.getVaultAddress(governanceAirdropState);
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([governanceAirdropState.toBuffer()], this.governanceVerifierProgram.programId);
            const governanceConfigureIx = yield this.airdropProgram.methods.configure(airdropSeed)
                .accounts({
                payer: authority,
                closeAuthority: authority,
                verifierSignature,
                vault: governanceVault,
                mint,
                state: governanceAirdropState,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                systemProgram: anchor_1.web3.SystemProgram.programId,
                rent: anchor_1.web3.SYSVAR_RENT_PUBKEY,
            })
                .instruction();
            // First instruction configures the airdrop program.
            transaction.add(governanceConfigureIx);
            const governanceInitIx = yield this.governanceVerifierProgram.methods
                .configure(verifierSeed, amountPerVoter, eligibilityStart, eligibilityEnd)
                .accounts({
                payer: authority,
                state: governanceVerifierState,
                airdropState: governanceAirdropState,
                governance,
                systemProgram: anchor_1.web3.SystemProgram.programId,
            })
                .instruction();
            // Next instruction configures the governance verifier.
            transaction.add(governanceInitIx);
            const transferIx = (0, spl_token_1.createTransferInstruction)(source, governanceVault, authority, totalAmount);
            transaction.add(transferIx);
            return {
                transaction,
                airdropState: governanceAirdropState,
                verifierState: governanceVerifierState,
            };
        });
    }
    /**
     * Create a transaction with the instructions for setting up an orca
     * airdrop. This includes the config as well as transferring the tokens.
     */
    createConfigOrcaTransaction(source, authority, totalAmount, pool, rewardIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const { mint } = yield (0, spl_token_1.getAccount)(this.connection, source);
            const airdropSeed = crypto_1.default.randomBytes(32);
            const verifierSeed = crypto_1.default.randomBytes(32);
            const [orcaAirdropState, _orcaAirdropStateBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([airdropSeed], this.airdropProgram.programId));
            const [orcaVerifierState, _orcaVerifierBump] = (anchor_1.web3.PublicKey.findProgramAddressSync([verifierSeed], this.orcaVerifierProgram.programId));
            const vault = this.getVaultAddress(orcaAirdropState);
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([orcaAirdropState.toBuffer()], this.orcaVerifierProgram.programId);
            const orcaConfigureIx = yield this.airdropProgram.methods.configure(airdropSeed)
                .accounts({
                payer: authority,
                verifierSignature,
                vault,
                mint,
                state: orcaAirdropState,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                systemProgram: anchor_1.web3.SystemProgram.programId,
                rent: anchor_1.web3.SYSVAR_RENT_PUBKEY,
            })
                .instruction();
            // First instruction configures the airdrop program.
            transaction.add(orcaConfigureIx);
            const orcaInitIx = yield this.orcaVerifierProgram.methods
                .init(verifierSeed, rewardIndex)
                .accounts({
                authority,
                state: orcaVerifierState,
                airdropState: orcaAirdropState,
                pool,
                systemProgram: anchor_1.web3.SystemProgram.programId,
            })
                .instruction();
            // Next instruction configures the orca verifier.
            transaction.add(orcaInitIx);
            const transferIx = (0, spl_token_1.createTransferInstruction)(source, vault, authority, totalAmount);
            transaction.add(transferIx);
            return {
                transaction,
                airdropState: orcaAirdropState,
                verifierState: orcaVerifierState,
            };
        });
    }
    /**
     * Create a transaction for recovering airdrop tokens that were not claimed.
     */
    createCloseTransaction(authority, state, recipient) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const vault = this.getVaultAddress(state);
            const closeIx = yield this.airdropProgram.methods.close()
                .accounts({
                authority,
                state,
                vault,
                recipient,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
            }).instruction();
            transaction.add(closeIx);
            return transaction;
        });
    }
    /**
     * Create a transaction with the instructions for setting up a basic claim.
     */
    createClaimBasicTransaction(airdropState, recipientTokenAccount, amount, authority) {
        return __awaiter(this, void 0, void 0, function* () {
            const transaction = new web3_js_1.Transaction();
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([airdropState.toBuffer()], this.basicVerifierProgram.programId);
            const vault = this.getVaultAddress(airdropState);
            const basicClaimIx = yield this.basicVerifierProgram.methods.claim(amount)
                .accounts({
                authority,
                airdropState,
                cpiAuthority: verifierSignature,
                vault,
                recipient: recipientTokenAccount,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                airdropProgram: this.airdropProgram.programId,
            }).instruction();
            transaction.add(basicClaimIx);
            return transaction;
        });
    }
    /**
     * Create a transaction with the instructions for setting up a password claim.
     */
    createClaimPasswordTransaction(verifierState, recipient, amount, authority, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const verifierStateObj = yield this.passwordVerifierProgram.account.verifierState.fetch(verifierState, 'single');
            const { airdropState } = verifierStateObj;
            const transaction = new web3_js_1.Transaction();
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([airdropState.toBuffer()], this.passwordVerifierProgram.programId);
            const vault = this.getVaultAddress(airdropState);
            const passwordClaimIx = (yield this.passwordVerifierProgram.methods.claim(amount, 
            // This is the data for the proof.
            Buffer.from(password))
                .accounts({
                authority,
                verifierState,
                cpiAuthority: verifierSignature,
                airdropState,
                vault,
                recipient,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                airdropProgram: this.airdropProgram.programId,
            }).instruction());
            transaction.add(passwordClaimIx);
            return transaction;
        });
    }
    /**
     * Create a transaction with the instructions for setting up a merkle claim.
     */
    createClaimMerkleTransaction(verifierState, recipient, amountsByRecipient, authority) {
        return __awaiter(this, void 0, void 0, function* () {
            const verifierStateObj = yield this.merkleVerifierProgram.account.verifierState.fetch(verifierState, 'single');
            const { airdropState } = verifierStateObj;
            const airdropStateObj = yield this.airdropProgram.account.state.fetch(airdropState, 'single');
            const vaultObj = yield (0, spl_token_1.getAccount)(this.connection, airdropStateObj.vault, 'single');
            const { mint } = vaultObj;
            const recipientTokenAccount = yield (0, spl_token_1.getAssociatedTokenAddress)(mint, recipient);
            const transaction = new web3_js_1.Transaction();
            // Possibly initialize the recipient token account.
            if (!(yield this.connection.getAccountInfo(recipientTokenAccount, 'single'))) {
                transaction.add((0, spl_token_1.createAssociatedTokenAccountInstruction)(authority, recipientTokenAccount, recipient, mint));
            }
            const vault = this.getVaultAddress(airdropState);
            const tree = new balance_tree_1.BalanceTree(amountsByRecipient);
            const index = amountsByRecipient.findIndex((element) => element.account.toBase58() === recipient.toBase58());
            const { amount } = amountsByRecipient[index];
            const proofStrings = tree.getProof(index, recipient, amount);
            const proofBytes = proofStrings.map((p) => (0, utils_1.toBytes32Array)(p));
            let verificationData = Buffer.allocUnsafe(8);
            verificationData.writeBigUInt64LE(BigInt(index));
            // Calculate the receipt here because the verification data conveniently is
            // a buffer with just index.
            const [receipt, _receiptBump] = anchor_1.web3.PublicKey.findProgramAddressSync([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('Receipt')),
                verifierState.toBuffer(),
                verificationData,
            ], exports.MERKLE_VERIFIER_PK);
            for (const proofElem of proofBytes) {
                verificationData = Buffer.concat([verificationData, Buffer.from(proofElem)]);
            }
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([airdropState.toBuffer()], this.merkleVerifierProgram.programId);
            const merkleClaimIx = yield this.merkleVerifierProgram.methods.claim(amount, verificationData)
                .accounts({
                authority,
                verifierState,
                cpiAuthority: verifierSignature,
                airdropState,
                vault,
                recipient: recipientTokenAccount,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                receipt,
                airdropProgram: this.airdropProgram.programId,
            })
                .instruction();
            transaction.add(merkleClaimIx);
            return transaction;
        });
    }
    /**
     * Create a transaction with the instructions for setting up a governance claim.
     */
    createClaimGovernanceTransaction(verifierState, recipient, authority) {
        return __awaiter(this, void 0, void 0, function* () {
            const verifierStateObj = yield this.governanceVerifierProgram.account.verifierState.fetch(verifierState, 'single');
            const { airdropState, governance, eligibilityStart, eligibilityEnd, } = verifierStateObj;
            const transaction = new web3_js_1.Transaction();
            const airdropStateObj = yield this.airdropProgram.account.state.fetch(airdropState, 'single');
            const vaultObj = yield (0, spl_token_1.getAccount)(this.connection, airdropStateObj.vault, 'single');
            const { mint } = vaultObj;
            const recipientTokenAccount = yield (0, spl_token_1.getAssociatedTokenAddress)(mint, recipient);
            // Possibly initialize the recipient token account.
            if (!(yield this.connection.getAccountInfo(recipientTokenAccount, 'single'))) {
                transaction.add((0, spl_token_1.createAssociatedTokenAccountInstruction)(authority, recipientTokenAccount, recipient, mint));
            }
            const realm = yield (0, governance_1.getRealmFromGovernance)(this.connection, governance);
            const communityMint = yield (0, governance_1.getCommunityMintFromRealm)(this.connection, realm);
            const tokenOwnerRecordAddress = yield (0, governance_1.getTokenOwnerRecordAddress)(governance_1.GOVERNANCE_PROGRAM_ID, realm, communityMint, recipient);
            const proposalFilters = [
                (0, governance_1.pubkeyFilter)(1, governance),
                (0, governance_1.pubkeyFilter)(33, communityMint),
            ];
            const proposals = yield this.connection.getProgramAccounts(governance_1.GOVERNANCE_PROGRAM_ID, {
                commitment: this.connection.commitment,
                filters: [
                    {
                        memcmp: {
                            offset: 0,
                            bytes: bs58_1.default.encode([governance_1.GovernanceAccountType.ProposalV2]),
                        },
                    },
                    ...proposalFilters.map((f) => ({
                        memcmp: { offset: f.offset, bytes: bs58_1.default.encode(f.bytes) },
                    })),
                ],
            });
            let proposal;
            for (const possibleProposal of proposals) {
                const possibleVoteRecord = yield (0, governance_1.getVoteRecordAddress)(governance_1.GOVERNANCE_PROGRAM_ID, possibleProposal.pubkey, tokenOwnerRecordAddress);
                // Check if it is in the range
                const deserializedProposal = (0, deserialize_1.deserializeBorsh)((0, deserialize_1.createGovernanceAccountSchema)(deserialize_1.ACCOUNT_VERSION_V2), deserialize_1.Proposal, possibleProposal.account.data);
                const { votingAt } = deserializedProposal;
                if (eligibilityEnd.toNumber() < votingAt.toNumber()
                    || eligibilityStart.toNumber() > votingAt.toNumber()) {
                    continue;
                }
                // TODO: Check if it already has a receipt
                if ((yield this.connection.getAccountInfo(possibleVoteRecord, 'single'))) {
                    // If the voteRecord exists, use that.
                    proposal = possibleProposal.pubkey;
                    break;
                }
                // Sleep 100ms to backoff load on the RPC.
                yield new Promise((r) => setTimeout(r, 10));
            }
            if (proposal === undefined) {
                throw new Error('Could not find VoteRecord');
            }
            const voteRecord = yield (0, governance_1.getVoteRecordAddress)(governance_1.GOVERNANCE_PROGRAM_ID, proposal, tokenOwnerRecordAddress);
            const [receipt, _receiptBump] = anchor_1.web3.PublicKey.findProgramAddressSync([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('Receipt')),
                verifierState.toBuffer(),
                voteRecord.toBuffer(),
            ], exports.GOVERNANCE_VERIFIER_PK);
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([airdropState.toBuffer()], this.governanceVerifierProgram.programId);
            const vault = this.getVaultAddress(airdropState);
            const governanceClaimIx = (yield this.governanceVerifierProgram.methods.claim()
                .accounts({
                authority,
                verifierState,
                recipient: recipientTokenAccount,
                governance,
                proposal,
                voteRecord,
                receipt,
                cpiAuthority: verifierSignature,
                airdropState,
                vault,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                airdropProgram: this.airdropProgram.programId,
                systemProgram: anchor_1.web3.SystemProgram.programId,
            }).instruction());
            transaction.add(governanceClaimIx);
            return transaction;
        });
    }
    /**
     * Create a transaction with the instructions for setting up an orca claim.
     */
    createClaimOrcaTransaction(verifierState, recipient, position, positionTokenAccount, authority) {
        return __awaiter(this, void 0, void 0, function* () {
            const verifierStateObj = yield this.orcaVerifierProgram.account.verifierState.fetch(verifierState);
            const { airdropState } = verifierStateObj;
            const transaction = new web3_js_1.Transaction();
            const airdropStateObj = yield this.airdropProgram.account.state.fetch(airdropState);
            const vaultObj = yield (0, spl_token_1.getAccount)(this.connection, airdropStateObj.vault);
            const { mint } = vaultObj;
            const recipientTokenAccount = yield (0, spl_token_1.getAssociatedTokenAddress)(mint, recipient);
            transaction.add((0, spl_token_1.createAssociatedTokenAccountIdempotentInstruction)(authority, recipientTokenAccount, recipient, mint));
            const [receipt, _receiptBump] = anchor_1.web3.PublicKey.findProgramAddressSync([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('Receipt')),
                verifierState.toBuffer(),
                position.toBuffer(),
            ], exports.ORCA_VERIFIER_PK);
            // Init receipt if needed
            if (!(yield this.connection.getAccountInfo(receipt))) {
                const initReceiptIx = yield this.orcaVerifierProgram.methods.initReceipt()
                    .accounts({
                    authority,
                    verifierState,
                    position,
                    receipt,
                    systemProgram: anchor_1.web3.SystemProgram.programId,
                }).instruction();
                transaction.add(initReceiptIx);
            }
            const [verifierSignature, _signatureBump] = anchor_1.web3.PublicKey.findProgramAddressSync([airdropState.toBuffer()], this.orcaVerifierProgram.programId);
            const vault = this.getVaultAddress(airdropState);
            const orcaClaimIx = (yield this.orcaVerifierProgram.methods.claim()
                .accounts({
                authority,
                verifierState,
                recipient: recipientTokenAccount,
                vault,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                airdropProgram: this.airdropProgram.programId,
                position,
                positionTokenAccount,
                receipt,
                cpiAuthority: verifierSignature,
                airdropState,
            }).instruction());
            transaction.add(orcaClaimIx);
            return transaction;
        });
    }
}
exports.Airdrop = Airdrop;
